import { io, type Socket } from 'socket.io-client';
import {
  CLOCK_SYNC_SAMPLES,
  type Ack,
  type ClientToServerEvents,
  type ServerToClientEvents,
} from '@sincronia/shared';
import { useGameStore } from '../store/gameStore.js';
import { dragBus } from './dragBus.js';

export type AppClientSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: AppClientSocket | null = null;
let bound = false;

export function getSocket(): AppClientSocket {
  if (!socket) {
    socket = io({
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 400,
      reconnectionDelayMax: 4000,
      timeout: 8000,
    });
    bindEvents(socket);
  }
  return socket;
}

/** Emite um evento com acknowledgement tipado. */
export function emitAck<T>(
  event: keyof ClientToServerEvents,
  payload: unknown,
  timeoutMs = 8000,
): Promise<Ack<T>> {
  const current = getSocket();
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve({
        ok: false,
        error: { code: 'INTERNAL_ERROR', message: 'Tempo esgotado aguardando o servidor.' },
      });
    }, timeoutMs);

    const emitter = current as unknown as {
      emit: (name: string, data: unknown, ack: (response: Ack<T>) => void) => void;
    };
    emitter.emit(String(event), payload, (response: Ack<T>) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(response);
    });
  });
}

/** Emite sem acknowledgement (eventos de arrasto). */
export function emitFast(event: keyof ClientToServerEvents, payload: unknown): void {
  const emitter = getSocket() as unknown as { emit: (name: string, data: unknown) => void };
  emitter.emit(String(event), payload);
}

function bindEvents(current: AppClientSocket): void {
  if (bound) return;
  bound = true;
  const store = useGameStore.getState;

  current.on('connect', () => {
    store().setConnection('connected');
    void syncClock();
  });

  current.on('disconnect', () => {
    store().setConnection('disconnected');
    dragBus.clear();
  });

  current.io.on('reconnect_attempt', () => store().setConnection('connecting'));
  current.on('connect_error', () => store().setConnection('disconnected'));

  current.on('room:snapshot', (snapshot) => {
    store().setSnapshot(snapshot);
    if (snapshot.phase !== 'PLAYING') dragBus.clear();
  });

  current.on('player:privateState', (privateState) => {
    store().setPrivateState(privateState);
    const pending = store().pendingCardId;
    if (pending && !privateState.hand.some((card) => card.id === pending)) {
      store().setPendingCard(null);
    }
  });

  current.on('room:error', (error) => store().pushToast(error.code));

  current.on('drag:started', ({ playerId, x, y }) => dragBus.start(playerId, x, y));
  current.on('drag:moved', ({ playerId, x, y }) => dragBus.move(playerId, x, y));
  current.on('drag:ended', ({ playerId }) => dragBus.end(playerId));

  current.on('card:accepted', (payload) => {
    store().setFlash({ kind: 'accepted', payload, at: Date.now() });
    dragBus.end(payload.playerId);
    if (payload.cardId === store().pendingCardId) store().setPendingCard(null);
  });

  current.on('card:rejected', (payload) => {
    store().setPendingCard(null);
    store().pushToast(payload.error.code);
    store().setFlash({
      kind: 'rejected',
      cardId: payload.cardId,
      code: payload.error.code,
      at: Date.now(),
    });
  });

  current.on('error:resolved', (summary) => {
    dragBus.clear();
    store().setFlash({ kind: 'error', payload: summary, at: Date.now() });
  });

  current.on('reward:granted', (reward) =>
    store().setFlash({ kind: 'reward', payload: reward, at: Date.now() }),
  );

  current.on('star:resolved', ({ approved, summary }) =>
    store().setFlash({ kind: 'star', payload: summary, approved, at: Date.now() }),
  );

  current.on('level:completed', (stats) =>
    store().setFlash({ kind: 'level', payload: stats, at: Date.now() }),
  );
}

/**
 * Sincronização de relógio: várias amostras, mediana dos offsets.
 * offset = serverNow - (t0 + t1) / 2
 */
export async function syncClock(samples = CLOCK_SYNC_SAMPLES): Promise<number | null> {
  const offsets: number[] = [];

  for (let index = 0; index < samples; index += 1) {
    const t0 = Date.now();
    const response = await emitAck<{ serverNow: number }>('time:sync', { t0 }, 4000);
    const t1 = Date.now();
    if (!response.ok) continue;
    offsets.push(response.data.serverNow - (t0 + t1) / 2);
  }

  if (offsets.length === 0) return null;

  offsets.sort((a, b) => a - b);
  const middle = Math.floor(offsets.length / 2);
  const median =
    offsets.length % 2 === 0
      ? ((offsets[middle - 1] ?? 0) + (offsets[middle] ?? 0)) / 2
      : (offsets[middle] ?? 0);

  useGameStore.getState().setClockOffset(median);
  return median;
}

export function disconnectSocket(): void {
  socket?.disconnect();
}
